/**
 * CmarkdownvisualError Component
 * The single failure surface for every recognized mermaid visual.
 *
 * A fence whose header the dispatcher recognized belongs to its component,
 * including when the body will not parse — so the block resolves to a
 * deliberate product state instead of vanishing from the deliverable. The raw
 * fence source is deliberately NOT rendered: readers cannot act on mermaid
 * syntax, and showing it would leak authoring detail into a finished document.
 */

'use client';

import type { ReactElement } from 'react';
import { Cmarkdownvisual } from './Cmarkdownvisual';
import {
  CmarkdownvisualErrorReason,
  type CmarkdownvisualErrorProps,
  type CmarkdownvisualType,
} from './Cmarkdownvisual.types';
import {
  ERROR_BODY_STYLE,
  ERROR_CONTENT_STYLE,
  ERROR_DETAIL_OPACITY,
  ERROR_DETAIL_SIZE,
  ERROR_HEADLINE_SIZE,
  ERROR_MARK_STYLE,
} from './CmarkdownvisualStyles';
import { Ctext } from '../Ctext';
import { TextPreset } from '@/constants/componentPresets';
import { FontWeight } from '@/theme/typography';

const DETAIL_BY_REASON: Record<CmarkdownvisualErrorReason, string> = {
  [CmarkdownvisualErrorReason.Unparseable]:
    'Its source does not match a shape this view can draw.',
  [CmarkdownvisualErrorReason.Empty]: 'Its source describes no content to draw.',
  [CmarkdownvisualErrorReason.NoValidLocations]:
    'Its source names no place or coordinate that can be plotted.',
};

const HEADLINE_BY_REASON: Record<
  CmarkdownvisualErrorReason,
  (type: CmarkdownvisualType) => string
> = {
  [CmarkdownvisualErrorReason.Unparseable]: (type) => `This ${type} could not be drawn`,
  [CmarkdownvisualErrorReason.Empty]: (type) => `This ${type} is empty`,
  [CmarkdownvisualErrorReason.NoValidLocations]: (type) =>
    `This ${type} has no locations`,
};

export function CmarkdownvisualError({
  type,
  reason,
}: CmarkdownvisualErrorProps): ReactElement {

  return (
      <Cmarkdownvisual type={type} contentStyle={ERROR_CONTENT_STYLE}>
        <div style={ERROR_BODY_STYLE}>
          <span style={ERROR_MARK_STYLE} />
          <Ctext
            preset={TextPreset.Strong}
            size={ERROR_HEADLINE_SIZE}
            weight={FontWeight.Medium}
            align="center"
          >
            {HEADLINE_BY_REASON[reason](type)}
          </Ctext>
          <Ctext
            preset={TextPreset.Mild}
            size={ERROR_DETAIL_SIZE}
            opacity={ERROR_DETAIL_OPACITY}
            align="center"
          >
            {DETAIL_BY_REASON[reason]}
          </Ctext>
        </div>
      </Cmarkdownvisual>
  );
}
